/**
 * VISIONS (1.2.0) — the one inclusion rule, the activity codes, and the shapes of the monthly Exhibit A report
 * (REQUIREMENTS.md §12, docs/VISIONS-EXHIBIT-A.md). Consent is the gate: nobody without it; with it, a tenant's
 * rows go in when registered with VISIONS, or 60 or older, or a referral was requested (or made). Reading &
 * Services never produces a row. ABF keeps the whole participation record for everyone; the report is the
 * filtered view.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------------------------------------
 * The inclusion rule
 * ---------------------------------------------------------------------------------------------- */

export interface VisionsAnswersLike {
  age60?: string | null;
  registered?: string | null;
  consent?: string | null;
  referral?: string | null;
  /** Staff marked the referral made (yyyy-mm-dd). */
  referralMadeOn?: string | null;
}

export type VisionsBecause = 'registered' | 'age60' | 'referral';
export type VisionsHeldBack = 'noAnswers' | 'noConsent' | 'noneOfThree';

export interface VisionsInclusion {
  onReport: boolean;
  /** Why they are on it (the report's columns L, F and N say which). */
  because: VisionsBecause[];
  /** Why they are not (null when on it). */
  why: VisionsHeldBack | null;
}

/**
 * The one rule. No answers or no consent → never. With consent → registered ("not sure" counts as no until
 * staff confirm), or 60 or older, or a referral requested or made. Otherwise held back ("not on the VISIONS
 * report — why").
 */
export function visionsInclusion(v: VisionsAnswersLike | null | undefined): VisionsInclusion {
  if (!v || !v.consent) return { onReport: false, because: [], why: 'noAnswers' };
  if (v.consent !== 'yes') return { onReport: false, because: [], why: 'noConsent' };
  const because: VisionsBecause[] = [];
  if (v.registered === 'yes') because.push('registered');
  if (v.age60 === 'yes') because.push('age60');
  if (v.referral === 'yes' || !!v.referralMadeOn) because.push('referral');
  return because.length ? { onReport: true, because, why: null } : { onReport: false, because, why: 'noneOfThree' };
}

/** The held-back reason in words (the Companion tab's "not on the VISIONS report — why"). */
export function heldBackWords(why: VisionsHeldBack, lang: 'en' | 'es' = 'en'): string {
  const en: Record<VisionsHeldBack, string> = {
    noAnswers: 'The VISIONS questions are not answered yet.',
    noConsent: 'Said no to sharing with VISIONS.',
    noneOfThree: 'Consented, but under 60, not registered with VISIONS and no referral asked for.',
  };
  const es: Record<VisionsHeldBack, string> = {
    noAnswers: 'Todavía no respondió las preguntas de VISIONS.',
    noConsent: 'Dijo que no a compartir con VISIONS.',
    noneOfThree: 'Dio su consentimiento, pero es menor de 60, no está inscrito en VISIONS y no pidió que lo conectemos.',
  };
  return (lang === 'es' ? es : en)[why];
}

/* ------------------------------------------------------------------------------------------------
 * Activity codes (the template's Lists sheet; editable in Settings, mirrored back into the workbook)
 * ---------------------------------------------------------------------------------------------- */

/** The Lists sheet's reporting groups (column I) — the Summary sheet's "by service group" rows. */
export const REPORTING_GROUPS = ['Wellness Walks', 'Escorted Outings', 'Community Engagement', 'Social / Companion', 'Referral Support', 'Other'] as const;
export type ReportingGroup = (typeof REPORTING_GROUPS)[number];

export interface ActivityCodeDef {
  code: string;
  description: string;
  group: ReportingGroup;
}

/** The template's own list (Downloads/7-Companion-Forms/Exhibit_A…xlsx, sheet "Lists", A2:C11). */
export const DEFAULT_ACTIVITY_CODES: ActivityCodeDef[] = [
  { code: 'WW', description: 'Individual Wellness Walk', group: 'Wellness Walks' },
  { code: 'GWW', description: 'Group Wellness Walk', group: 'Wellness Walks' },
  { code: 'CE', description: 'Community Engagement Outing', group: 'Community Engagement' },
  { code: 'SA', description: 'Shopping Assistance', group: 'Escorted Outings' },
  { code: 'PE', description: 'Pharmacy Escort', group: 'Escorted Outings' },
  { code: 'NE', description: 'Neighborhood Errand', group: 'Escorted Outings' },
  { code: 'SI', description: 'Social Interaction / Companion Visit', group: 'Social / Companion' },
  { code: 'WP', description: 'Walk With Ease Activity', group: 'Wellness Walks' },
  { code: 'REF', description: 'Referral / Enrollment Support', group: 'Referral Support' },
  { code: 'OTH', description: 'Other', group: 'Other' },
];

/** The template's CodeList covers ten rows (Lists!A2:A11); the list stays within it. */
export const MAX_ACTIVITY_CODES = 10;

export const ActivityCodesSchema = z
  .array(
    z.object({
      code: z.string().trim().toUpperCase().regex(/^[A-Z]{1,6}$/, 'code'),
      description: z.string().trim().min(1).max(80),
      group: z.enum(REPORTING_GROUPS),
    }),
  )
  .min(1)
  .max(MAX_ACTIVITY_CODES)
  .superRefine((list, ctx) => {
    const seen = new Set<string>();
    list.forEach((c, i) => {
      if (seen.has(c.code)) ctx.addIssue({ code: 'custom', path: [i, 'code'], message: 'duplicate' });
      seen.add(c.code);
    });
  });

/** Codes staff can log by hand (the rest come from the requests and the outings). */
export const LOGGABLE_CODES_HINT = ['REF', 'WP', 'OTH', 'GWW', 'SI'] as const;

/* ------------------------------------------------------------------------------------------------
 * A staff-logged activity (REF, WP, OTH … — whatever the request cards and outings do not produce)
 * ---------------------------------------------------------------------------------------------- */

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const LogActivitySchema = z.object({
  tenantId: z.string().trim().min(1).max(50),
  day: isoDay,
  code: z.string().trim().toUpperCase().regex(/^[A-Z]{1,6}$/),
  minutes: z.number().int().min(1).max(600).nullable().default(null),
  followUp: z.boolean().default(false),
  notes: z.string().trim().max(300).default(''),
});
export type LogActivityInput = z.input<typeof LogActivitySchema>;

export const ReferralMadeSchema = z.object({ tenantId: z.string().trim().min(1).max(50), day: isoDay, logActivity: z.boolean().default(true) });

/* ------------------------------------------------------------------------------------------------
 * Months
 * ---------------------------------------------------------------------------------------------- */

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

export const isMonthKey = (m: string) => /^\d{4}(0[1-9]|1[0-2])$/.test(m);
export const monthNameOf = (yyyymm: string) => MONTH_NAMES[Number(yyyymm.slice(4, 6)) - 1] ?? '';
/** The month before a service day key (yyyyMMdd → yyyyMM). */
export function previousMonth(dayKey: string): string {
  const y = Number(dayKey.slice(0, 4));
  const m = Number(dayKey.slice(4, 6));
  return m === 1 ? `${y - 1}12` : `${y}${String(m - 1).padStart(2, '0')}`;
}

/* ------------------------------------------------------------------------------------------------
 * The API's shapes
 * ---------------------------------------------------------------------------------------------- */

export type ActivitySource = 'visit' | 'outing' | 'logged';

/** One Companion activity for one tenant (ABF's own record — everyone, whatever the VISIONS answers). */
export interface CompanionActivity {
  id: string;
  source: ActivitySource;
  tenantId: string;
  tenantName: string;
  unit: string;
  dayKey: string;
  code: string;
  minutes: number | null;
  /** Where the minutes came from: the completion form, the scheduled length, staff, or nowhere. */
  durationSource: 'form' | 'scheduled' | 'staff' | 'none';
  /** The volunteer(s), first names (the sheet's sample). */
  volunteer: string;
  followUp: boolean;
  notes: string;
}

/** One row of the Exhibit A master sheet (columns A–P). */
export interface ExhibitRow {
  name: string;
  apt: string;
  phone: string;
  email: string;
  age: number | null;
  /** Written to F only when E is blank. */
  age60: 'Yes' | 'No' | '';
  dayKey: string;
  code: string;
  minutes: number | null;
  volunteer: string;
  registered: 'Yes' | 'No';
  consent: 'Yes' | 'No';
  referral: 'Yes' | 'No';
  followUp: 'Yes' | 'No';
  notes: string;
}

export interface ParticipationView {
  month: string;
  people: number;
  activities: number;
  minutes: number;
  byCode: { code: string; description: string; activities: number; people: number; minutes: number }[];
}

export interface HeldBackView {
  tenantId: string;
  name: string;
  unit: string;
  why: VisionsHeldBack;
  /** This month's Companion activities that stay off the report. */
  activities: number;
}

/** 26 Oct 2026 (D-1026-VISIONS): a past Companion visit nobody has closed out — held OFF the report until someone does. */
export interface WaitingCloseoutView {
  appointmentId: string;
  volunteerId: string;
  /** The Companion request it belongs to (the volunteer's first name is for the office's eyes). */
  requestId: string;
  tenantId: string;
  name: string;
  unit: string;
  dayKey: string;
  code: string;
  volunteer: string;
  minutes: number;
}

export interface ReportVersionView {
  version: number;
  name: string;
  path: string;
  at: string;
  by: string;
  rows: number;
  people: number;
}

export interface VisionsReportView {
  month: string;
  monthName: string;
  year: number;
  people: number;
  activities: number;
  sixtyPlus: number;
  referrals: number;
  rows: (ExhibitRow & { tenantId: string; activityId: string })[];
  heldBack: HeldBackView[];
  /** Activities of tenants without consent (a count only — never named here). */
  notConsentedActivities: number;
  /** 26 Oct 2026 (D-1026-VISIONS): past visits of people who belong on the report but have no close-out yet — not counted. */
  waitingCloseout: WaitingCloseoutView[];
  participation: ParticipationView;
  versions: ReportVersionView[];
  sent: { at: string; by: string; to: string; version: number; communicationId: string | null; sendAt: string } | null;
  liaison: string | null;
  codes: ActivityCodeDef[];
  template: 'settings' | 'default' | 'missing';
  folder: string;
}

export interface ReferralTaskView {
  tenantId: string;
  name: string;
  unit: string;
  requestedAt: string | null;
}
